import maplibregl from 'maplibre-gl';

const EARTH_RADIUS_M = 6371000;
const EFFECTIVE_EARTH_RADIUS_M = EARTH_RADIUS_M * (4 / 3);
const HARD_MAX_RANGE_CAP_M = 10000;

const EMPTY_FEATURE_COLLECTION = Object.freeze({
    type: 'FeatureCollection',
    features: []
});

const TEKTELIC_ANTENNA_PROFILES = Object.freeze({
    'tektelic-external-2.5dbi': {
        gainDbi: 2.5,
        verticalBeamwidthDeg: 45,
        label: 'TEKTELIC external omni 2.5 dBi'
    },
    'tektelic-external-6dbi': {
        gainDbi: 6,
        verticalBeamwidthDeg: 20,
        label: 'TEKTELIC external omni 6 dBi'
    },
    'tektelic-external-8.5dbi': {
        gainDbi: 8.5,
        verticalBeamwidthDeg: 14,
        label: 'TEKTELIC external omni 8.5 dBi'
    }
});

const DEFAULT_CLUTTER_LOSS_BY_CLASS_DB = Object.freeze({
    open: 1.5,
    rural: 2.5,
    suburban: 5.5,
    urban: 9,
    denseUrban: 14,
    industrial: 11,
    forest: 12
});

function finiteNumber(value, fallback) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
}

function clampNumber(value, min, max) {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return min;
    return Math.max(min, Math.min(max, numberValue));
}

export default class PathLossCoverageLayer {
    constructor(options = {}) {
        this.map = options.map || null;

        this.sourceId = options.sourceId || 'p452-path-loss-coverage';
        this.contourSourceId = options.contourSourceId || 'p452-path-loss-contours';
        this.fillLayerId = options.fillLayerId || 'p452-path-loss-coverage-fill';
        this.borderLayerId = options.borderLayerId || 'p452-path-loss-coverage-border';
        this.contourLayerId = options.contourLayerId || 'p452-path-loss-contours';
        this.dotSourceId = options.dotSourceId || 'p452-path-loss-dot-grid';
        this.dotLayerId = options.dotLayerId || 'p452-path-loss-dot-grid';

        this.frequencyMhz = finiteNumber(options.frequencyMhz, 868);
        this.minRangeM = Math.max(1, finiteNumber(options.minRangeM, 120));
        this.maxRangeCapM = Math.max(
            this.minRangeM,
            Math.min(finiteNumber(options.maxRangeCapM, HARD_MAX_RANGE_CAP_M), HARD_MAX_RANGE_CAP_M)
        );
        this.maxRangeM = clampNumber(
            finiteNumber(options.maxRangeM, HARD_MAX_RANGE_CAP_M),
            this.minRangeM,
            this.maxRangeCapM
        );

        this.rayStepDeg = clampNumber(finiteNumber(options.rayStepDeg, 6), 1, 30);
        this.distanceStepM = clampNumber(finiteNumber(options.distanceStepM, 250), 50, 1000);
        this.maxTerrainSamples = clampNumber(finiteNumber(options.maxTerrainSamples, 14), 4, 40);

        // Dot-grid visualisation for the modelled path-loss coverage. The dots are
        // generated only for path positions that still have non-negative link margin.
        this.showDotGrid = options.showDotGrid !== false;
        this.dotGridSpacingM = clampNumber(finiteNumber(options.dotGridSpacingM, 500), 100, 2000);
        this.dotOpacity = clampNumber(finiteNumber(options.dotOpacity, 0.5), 0, 1);
        this.dotRadiusBase = clampNumber(finiteNumber(options.dotRadiusBase, 2.1), 0.5, 8);
        this.maxDotFeaturesPerGateway = clampNumber(
            finiteNumber(options.maxDotFeaturesPerGateway, 1400),
            100,
            5000
        );

        this.gatewayModel = options.gatewayModel || 'TEKTELIC KONA Macro/Mega';
        this.gatewayAntennaProfile =
            options.gatewayAntennaProfile || 'tektelic-external-6dbi';

        const selectedAntennaProfile =
            TEKTELIC_ANTENNA_PROFILES[this.gatewayAntennaProfile] ||
            TEKTELIC_ANTENNA_PROFILES['tektelic-external-6dbi'];

        this.antennaType = options.antennaType || 'external omnidirectional';
        this.gatewayAntennaLabel = options.gatewayAntennaLabel || selectedAntennaProfile.label;
        this.gatewayAntennaVerticalBeamwidthDeg = finiteNumber(
            options.gatewayAntennaVerticalBeamwidthDeg,
            selectedAntennaProfile.verticalBeamwidthDeg
        );

        this.deviceTxPowerDbm = finiteNumber(options.deviceTxPowerDbm, 14);
        this.deviceAntennaGainDbi = finiteNumber(options.deviceAntennaGainDbi, 0);
        this.gatewayAntennaGainDbi = finiteNumber(
            options.gatewayAntennaGainDbi,
            selectedAntennaProfile.gainDbi
        );

        this.receiverSensitivityDbm = finiteNumber(options.receiverSensitivityDbm, -142);
        this.referenceSpreadingFactor = options.referenceSpreadingFactor || 'SF12';

        this.fadeMarginDb = finiteNumber(options.fadeMarginDb, 14);

        this.receiverHeightM = finiteNumber(options.receiverHeightM, 1.2);
        this.defaultGatewayHeightM = finiteNumber(options.defaultGatewayHeightM, 3);

        this.clutterLossByClassDb = {
            ...DEFAULT_CLUTTER_LOSS_BY_CLASS_DB,
            ...(options.clutterLossByClassDb || {})
        };

        this.defaultClutterClass = this.normalizeClutterClass(
            options.defaultClutterClass || 'suburban'
        );

        this.fallbackClutterLossDb = finiteNumber(
            options.clutterLossDb,
            this.clutterLossByClassDb[this.defaultClutterClass]
        );

        this.clutterResolver =
            typeof options.clutterResolver === 'function' ? options.clutterResolver : null;

        this.terrainExaggeration = finiteNumber(options.terrainExaggeration, 5);
        this.divideTerrainByExaggeration = options.divideTerrainByExaggeration !== false;

        this.visible = options.visible !== false;
        this.lastGatewayCount = 0;
        this.lastFeatureCount = 0;
        this.lastUsedTerrainSamples = 0;

        this.handlersAttached = false;
        this.onFillClick = this.handleFillClick.bind(this);
        this.onMouseEnter = this.handleMouseEnter.bind(this);
        this.onMouseLeave = this.handleMouseLeave.bind(this);
    }

    setMap(map) {
        this.map = map || null;
    }

    getLayerIds() {
        return [this.fillLayerId, this.borderLayerId, this.contourLayerId, this.dotLayerId];
    }

    async update({ gateways = [], beforeId = undefined, onProgress } = {}) {
        if (!this.map) return;

        const gatewayList = Array.isArray(gateways)
            ? gateways
            : Array.from(gateways?.values?.() || []);

        const { coverageFeatures, contourFeatures, dotFeatures } = await this.buildCoverage(gatewayList, onProgress);
        if (!this.map) return;

        this.ensureSources();
        this.setSourceData(coverageFeatures, contourFeatures, dotFeatures);
        this.ensureLayers(beforeId);
        this.setVisible(this.visible);

        this.lastGatewayCount = gatewayList.length;
        this.lastFeatureCount = coverageFeatures.length + dotFeatures.length;
    }

    async buildCoverage(gateways, onProgress) {
        const coverageFeatures = [];
        const contourFeatures = [];
        const dotFeatures = [];
        this.lastUsedTerrainSamples = 0;

        for (const [index, gateway] of gateways.entries()) {
            if (onProgress) await onProgress(index, gateways.length);
            if (!this.map) break;
            const model = this.calculateGatewayCoverage(gateway);
            if (!model) continue;

            coverageFeatures.push(model.coverageFeature);
            contourFeatures.push(...model.contourFeatures);
            dotFeatures.push(...model.dotFeatures);
        }

        return { coverageFeatures, contourFeatures, dotFeatures };
    }

    calculateGatewayCoverage(gateway) {
        const gatewayLat = Number.parseFloat(gateway?.lat);
        const gatewayLon = Number.parseFloat(gateway?.lon);

        if (!Number.isFinite(gatewayLat) || !Number.isFinite(gatewayLon)) {
            return null;
        }

        const groundElevation = this.resolveGatewayGroundElevation(gatewayLon, gatewayLat, gateway);
        const gatewayAltitude = this.resolveGatewayAltitude(gateway, groundElevation);
        const towerHeight = Math.max(0, gatewayAltitude - groundElevation);
        const allowedPathLossDb = this.getAllowedPathLossDb();

        const raySamples = [];
        const rayDotSamples = [];
        const clutterClassCounts = new Map();

        let blockedRays = 0;
        let terrainDataRays = 0;
        let sumPathLoss = 0;
        let pathLossSamples = 0;
        let sumClutterLoss = 0;
        let clutterSamples = 0;
        let sumTerrainLoss = 0;
        let terrainLossSamples = 0;

        for (let bearingDeg = 0; bearingDeg < 360; bearingDeg += this.rayStepDeg) {
            const result = this.findReachableDistance({
                gateway,
                gatewayLat,
                gatewayLon,
                gatewayAltitude,
                bearingDeg,
                allowedPathLossDb
            });

            if (result.hadTerrainData) terrainDataRays += 1;
            if (result.blocked) blockedRays += 1;

            if (Number.isFinite(result.pathLossDb)) {
                sumPathLoss += result.pathLossDb;
                pathLossSamples += 1;
            }

            if (Number.isFinite(result.clutterLossDb)) {
                sumClutterLoss += result.clutterLossDb;
                clutterSamples += 1;
            }

            if (Number.isFinite(result.terrainLossDb)) {
                sumTerrainLoss += result.terrainLossDb;
                terrainLossSamples += 1;
            }

            if (result.clutterClass) {
                clutterClassCounts.set(
                    result.clutterClass,
                    (clutterClassCounts.get(result.clutterClass) || 0) + 1
                );
            }

            if (Array.isArray(result.dotSamples) && result.dotSamples.length > 0) {
                rayDotSamples.push({
                    bearingDeg,
                    samples: result.dotSamples
                });
            }

            const distance = Math.max(
                this.minRangeM,
                Math.min(result.distanceM, this.maxRangeM)
            );

            const endpoint = this.destinationPoint(gatewayLat, gatewayLon, bearingDeg, distance);
            if (!endpoint) continue;

            raySamples.push({
                bearingDeg,
                distanceM: distance,
                pathLossDb: result.pathLossDb,
                marginDb: result.marginDb,
                freeSpaceLossDb: result.freeSpaceLossDb,
                terrainLossDb: result.terrainLossDb,
                clutterLossDb: result.clutterLossDb,
                clutterClass: result.clutterClass,
                blocked: result.blocked,
                endpoint
            });
        }

        if (raySamples.length < 3) {
            return null;
        }

        const ring = raySamples.map((sample) => [sample.endpoint.lon, sample.endpoint.lat]);
        ring.push(ring[0]);

        const maxRangeM = Math.max(...raySamples.map((sample) => sample.distanceM));
        const averageRangeM =
            raySamples.reduce((sum, sample) => sum + sample.distanceM, 0) / raySamples.length;

        const averagePathLossDb = pathLossSamples > 0 ? sumPathLoss / pathLossSamples : null;
        const averageClutterLossDb = clutterSamples > 0 ? sumClutterLoss / clutterSamples : null;
        const averageTerrainLossDb = terrainLossSamples > 0 ? sumTerrainLoss / terrainLossSamples : null;

        const terrainBlockedRatio = raySamples.length > 0 ? blockedRays / raySamples.length : 0;
        const terrainAvailabilityRatio = raySamples.length > 0 ? terrainDataRays / raySamples.length : 0;
        const dominantClutterClass = this.getDominantClutterClass(clutterClassCounts);

        const confidenceScore = this.clamp(
            0.2 + terrainAvailabilityRatio * 0.55 + (1 - terrainBlockedRatio) * 0.2,
            0,
            1
        );

        const commonProperties = {
            gateway_id: String(gateway?.id ?? 'unknown'),
            coverage_type: 'p452_path_loss',
            method: 'p452_inspired_frontend',
            antenna_type: this.antennaType,
            antenna_profile: this.gatewayAntennaProfile,
            antenna_label: this.gatewayAntennaLabel,
            antenna_vertical_beamwidth_deg: this.gatewayAntennaVerticalBeamwidthDeg,
            gateway_model: this.gatewayModel,
            frequency_mhz: this.frequencyMhz,
            reference_spreading_factor: this.referenceSpreadingFactor,
            max_range_m: maxRangeM,
            hard_range_cap_m: this.maxRangeCapM,
            average_range_m: averageRangeM,
            allowed_path_loss_db: allowedPathLossDb,
            average_path_loss_db: averagePathLossDb,
            average_terrain_loss_db: averageTerrainLossDb,
            average_clutter_loss_db: averageClutterLossDb,
            default_clutter_class: this.defaultClutterClass,
            dominant_clutter_class: dominantClutterClass,
            terrain_blocked_ratio: terrainBlockedRatio,
            terrain_availability_ratio: terrainAvailabilityRatio,
            confidence_score: confidenceScore,
            ground_elevation: groundElevation,
            tower_height: towerHeight,
            gateway_altitude: gatewayAltitude,
            device_tx_power_dbm: this.deviceTxPowerDbm,
            device_antenna_gain_dbi: this.deviceAntennaGainDbi,
            gateway_antenna_gain_dbi: this.gatewayAntennaGainDbi,
            receiver_sensitivity_dbm: this.receiverSensitivityDbm,
            fade_margin_db: this.fadeMarginDb,
            receiver_height_m: this.receiverHeightM,
            visual_terrain_exaggeration: this.terrainExaggeration,
            dot_grid_spacing_m: this.dotGridSpacingM,
            dot_opacity: this.dotOpacity,
            deviceCount: Number(gateway?.deviceCount || gateway?.devices?.length || 0),
            readingDeviceCount: Number(gateway?.readingDeviceCount || 0)
        };

        const coverageFeature = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [ring]
            },
            properties: commonProperties
        };

        const contourFeatures = this.createContourFeatures(
            gatewayLat,
            gatewayLon,
            raySamples,
            commonProperties
        );

        const dotFeatures = this.createDotGridFeatures(
            gatewayLat,
            gatewayLon,
            rayDotSamples,
            commonProperties
        );

        return { coverageFeature, contourFeatures, dotFeatures };
    }

    findReachableDistance({
                              gateway,
                              gatewayLat,
                              gatewayLon,
                              gatewayAltitude,
                              bearingDeg,
                              allowedPathLossDb
                          }) {
        let lastGoodDistanceM = 0;
        let lastGoodPathLossDb = null;
        let lastGoodMarginDb = null;
        let lastGoodFreeSpaceLossDb = null;
        let lastGoodTerrainLossDb = null;
        let lastGoodClutterLossDb = null;
        let lastGoodClutterClass = null;
        const dotSamples = [];
        let nextDotDistanceM = this.dotGridSpacingM;
        let hadTerrainData = false;
        let blocked = false;

        for (
            let distanceM = this.distanceStepM;
            distanceM <= this.maxRangeM;
            distanceM += this.distanceStepM
        ) {
            const evaluation = this.evaluatePath({
                gateway,
                gatewayLat,
                gatewayLon,
                gatewayAltitude,
                bearingDeg,
                distanceM,
                allowedPathLossDb
            });

            if (evaluation.hadTerrainData) {
                hadTerrainData = true;
            }

            if (evaluation.marginDb >= 0) {
                lastGoodDistanceM = distanceM;
                lastGoodPathLossDb = evaluation.totalPathLossDb;
                lastGoodMarginDb = evaluation.marginDb;
                lastGoodFreeSpaceLossDb = evaluation.freeSpaceLossDb;
                lastGoodTerrainLossDb = evaluation.terrainLossDb;
                lastGoodClutterLossDb = evaluation.clutterLossDb;
                lastGoodClutterClass = evaluation.clutterClass;

                if (this.showDotGrid && distanceM + 0.001 >= nextDotDistanceM) {
                    dotSamples.push({
                        distanceM,
                        pathLossDb: evaluation.totalPathLossDb,
                        marginDb: evaluation.marginDb,
                        freeSpaceLossDb: evaluation.freeSpaceLossDb,
                        terrainLossDb: evaluation.terrainLossDb,
                        clutterLossDb: evaluation.clutterLossDb,
                        clutterClass: evaluation.clutterClass
                    });
                    nextDotDistanceM += this.dotGridSpacingM;
                }
            } else {
                blocked = evaluation.blockedByTerrain || evaluation.marginDb < -6;
                break;
            }
        }

        if (lastGoodDistanceM <= 0) {
            const minEvaluation = this.evaluatePath({
                gateway,
                gatewayLat,
                gatewayLon,
                gatewayAltitude,
                bearingDeg,
                distanceM: this.minRangeM,
                allowedPathLossDb
            });

            return {
                distanceM: this.minRangeM,
                pathLossDb: minEvaluation.totalPathLossDb,
                marginDb: minEvaluation.marginDb,
                freeSpaceLossDb: minEvaluation.freeSpaceLossDb,
                terrainLossDb: minEvaluation.terrainLossDb,
                clutterLossDb: minEvaluation.clutterLossDb,
                clutterClass: minEvaluation.clutterClass,
                dotSamples: minEvaluation.marginDb >= 0
                    ? [{
                        distanceM: this.minRangeM,
                        pathLossDb: minEvaluation.totalPathLossDb,
                        marginDb: minEvaluation.marginDb,
                        freeSpaceLossDb: minEvaluation.freeSpaceLossDb,
                        terrainLossDb: minEvaluation.terrainLossDb,
                        clutterLossDb: minEvaluation.clutterLossDb,
                        clutterClass: minEvaluation.clutterClass
                    }]
                    : [],
                hadTerrainData: hadTerrainData || minEvaluation.hadTerrainData,
                blocked: true
            };
        }

        return {
            distanceM: Math.min(lastGoodDistanceM, this.maxRangeM),
            pathLossDb: lastGoodPathLossDb,
            marginDb: lastGoodMarginDb,
            freeSpaceLossDb: lastGoodFreeSpaceLossDb,
            terrainLossDb: lastGoodTerrainLossDb,
            clutterLossDb: lastGoodClutterLossDb,
            clutterClass: lastGoodClutterClass,
            dotSamples,
            hadTerrainData,
            blocked
        };
    }

    evaluatePath({
                     gateway,
                     gatewayLat,
                     gatewayLon,
                     gatewayAltitude,
                     bearingDeg,
                     distanceM,
                     allowedPathLossDb
                 }) {
        const frequencyMhz = Math.max(1, this.frequencyMhz);
        const distanceKm = Math.max(0.001, distanceM / 1000);

        const freeSpaceLossDb =
            32.44 +
            20 * Math.log10(frequencyMhz) +
            20 * Math.log10(distanceKm);

        const endpoint = this.destinationPoint(gatewayLat, gatewayLon, bearingDeg, distanceM);

        if (!endpoint) {
            const clutter = this.resolveClutterLoss({
                gateway,
                endpoint: null,
                bearingDeg,
                distanceM
            });

            const totalPathLossDb = freeSpaceLossDb + clutter.lossDb;

            return {
                totalPathLossDb,
                marginDb: allowedPathLossDb - totalPathLossDb,
                freeSpaceLossDb,
                terrainLossDb: 0,
                clutterLossDb: clutter.lossDb,
                clutterClass: clutter.className,
                hadTerrainData: false,
                blockedByTerrain: false
            };
        }

        const receiverGround = this.getPhysicalTerrainElevation(endpoint.lon, endpoint.lat);
        const receiverAltitude = (receiverGround ?? 0) + this.receiverHeightM;

        const terrain = this.calculateTerrainPenalty({
            gatewayLat,
            gatewayLon,
            gatewayAltitude,
            endpointLat: endpoint.lat,
            endpointLon: endpoint.lon,
            receiverAltitude,
            distanceM
        });

        const clutter = this.resolveClutterLoss({
            gateway,
            endpoint,
            bearingDeg,
            distanceM
        });

        const totalPathLossDb = freeSpaceLossDb + terrain.lossDb + clutter.lossDb;
        const marginDb = allowedPathLossDb - totalPathLossDb;

        return {
            totalPathLossDb,
            marginDb,
            freeSpaceLossDb,
            terrainLossDb: terrain.lossDb,
            clutterLossDb: clutter.lossDb,
            clutterClass: clutter.className,
            hadTerrainData: terrain.hadTerrainData,
            blockedByTerrain: terrain.blockedByTerrain
        };
    }

    calculateTerrainPenalty({
                                gatewayLat,
                                gatewayLon,
                                gatewayAltitude,
                                endpointLat,
                                endpointLon,
                                receiverAltitude,
                                distanceM
                            }) {
        const sampleCount = Math.max(4, Math.floor(this.maxTerrainSamples));
        const wavelengthM = 300 / Math.max(1, this.frequencyMhz);
        const profileSamples = [];

        for (let index = 1; index < sampleCount; index += 1) {
            const fraction = index / sampleCount;

            const lon = gatewayLon + (endpointLon - gatewayLon) * fraction;
            const lat = gatewayLat + (endpointLat - gatewayLat) * fraction;

            const terrainElevation = this.getPhysicalTerrainElevation(lon, lat);
            if (terrainElevation === null || terrainElevation === undefined) {
                continue;
            }

            this.lastUsedTerrainSamples += 1;

            const d1 = distanceM * fraction;
            const d2 = distanceM - d1;

            if (d1 <= 0 || d2 <= 0) {
                continue;
            }

            const earthBulgeM = (d1 * d2) / (2 * EFFECTIVE_EARTH_RADIUS_M);
            const obstacleAltitudeM = terrainElevation + earthBulgeM;
            const lineAltitudeM =
                gatewayAltitude + (receiverAltitude - gatewayAltitude) * fraction;

            const fresnelRadiusM = Math.sqrt(
                Math.max(0, (wavelengthM * d1 * d2) / Math.max(distanceM, 1))
            );

            const requiredClearanceM = 0.6 * fresnelRadiusM;
            const actualClearanceM = lineAltitudeM - obstacleAltitudeM;
            const clearanceDeficitM = requiredClearanceM - actualClearanceM;
            const losObstructionM = obstacleAltitudeM - lineAltitudeM;

            const diffractionV =
                losObstructionM *
                Math.sqrt((2 * distanceM) / Math.max(wavelengthM * d1 * d2, 0.000001));

            const txElevationAngleRad = Math.atan2(
                obstacleAltitudeM - gatewayAltitude,
                d1
            );

            const rxElevationAngleRad = Math.atan2(
                obstacleAltitudeM - receiverAltitude,
                d2
            );

            profileSamples.push({
                fraction,
                d1,
                d2,
                terrainElevation,
                earthBulgeM,
                obstacleAltitudeM,
                lineAltitudeM,
                fresnelRadiusM,
                requiredClearanceM,
                actualClearanceM,
                clearanceDeficitM,
                losObstructionM,
                diffractionV,
                txElevationAngleRad,
                rxElevationAngleRad
            });
        }

        if (profileSamples.length === 0) {
            return {
                lossDb: 0,
                hadTerrainData: false,
                blockedByTerrain: false
            };
        }

        return this.calculateBullingtonStyleTerrainLoss(profileSamples);
    }

    calculateBullingtonStyleTerrainLoss(profileSamples) {
        let maxV = -Infinity;
        let worstClearanceDeficitM = 0;
        let cumulativeClearanceDeficitM = 0;
        let obstructionSamples = 0;
        let maxTxElevationAngleRad = -Infinity;
        let maxRxElevationAngleRad = -Infinity;

        for (const sample of profileSamples) {
            maxV = Math.max(maxV, sample.diffractionV);
            maxTxElevationAngleRad = Math.max(maxTxElevationAngleRad, sample.txElevationAngleRad);
            maxRxElevationAngleRad = Math.max(maxRxElevationAngleRad, sample.rxElevationAngleRad);

            if (sample.clearanceDeficitM > 0) {
                worstClearanceDeficitM = Math.max(worstClearanceDeficitM, sample.clearanceDeficitM);
                cumulativeClearanceDeficitM += sample.clearanceDeficitM;
                obstructionSamples += 1;
            }
        }

        const bullingtonLikeLossDb = this.diffractionLossFromV(maxV);

        const averageClearanceDeficitM =
            obstructionSamples > 0 ? cumulativeClearanceDeficitM / obstructionSamples : 0;

        const fresnelClearanceLossDb =
            worstClearanceDeficitM > 0
                ? Math.min(12, 20 * Math.log10(1 + worstClearanceDeficitM / 6))
                : 0;

        const multiObstaclePenaltyDb =
            obstructionSamples > 1
                ? Math.min(
                    14,
                    Math.log1p(obstructionSamples) * 2.5 + averageClearanceDeficitM * 0.05
                )
                : 0;

        const angularSpreadPenaltyDb =
            Number.isFinite(maxTxElevationAngleRad) && Number.isFinite(maxRxElevationAngleRad)
                ? Math.min(
                    8,
                    Math.max(0, (maxTxElevationAngleRad + maxRxElevationAngleRad) * (180 / Math.PI)) * 0.18
                )
                : 0;

        const primaryLossDb = Math.max(bullingtonLikeLossDb, fresnelClearanceLossDb);

        const lossDb = Math.min(
            70,
            primaryLossDb + multiObstaclePenaltyDb + angularSpreadPenaltyDb
        );

        return {
            lossDb,
            hadTerrainData: true,
            blockedByTerrain: worstClearanceDeficitM > 0 || bullingtonLikeLossDb > 0.1
        };
    }

    diffractionLossFromV(v) {
        if (!Number.isFinite(v) || v <= -0.78) {
            return 0;
        }

        return 6.9 + 20 * Math.log10(Math.sqrt((v - 0.1) ** 2 + 1) + v - 0.1);
    }

    createDotGridFeatures(gatewayLat, gatewayLon, rayDotSamples, commonProperties) {
        if (!this.showDotGrid || !Array.isArray(rayDotSamples) || rayDotSamples.length === 0) {
            return [];
        }

        const features = [];
        const maxFeatures = Math.max(100, Math.floor(this.maxDotFeaturesPerGateway));
        const candidateCount = rayDotSamples.reduce((sum, ray) => sum + (ray.samples?.length || 0), 0);
        const thinningRatio = candidateCount > maxFeatures ? candidateCount / maxFeatures : 1;

        let candidateIndex = 0;

        for (const ray of rayDotSamples) {
            if (!Array.isArray(ray.samples)) continue;

            for (const sample of ray.samples) {
                candidateIndex += 1;

                if (thinningRatio > 1 && candidateIndex % Math.ceil(thinningRatio) !== 0) {
                    continue;
                }

                const endpoint = this.destinationPoint(
                    gatewayLat,
                    gatewayLon,
                    ray.bearingDeg,
                    sample.distanceM
                );

                if (!endpoint) continue;

                const qualityClass = this.getDotQualityClass(sample.marginDb);

                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [endpoint.lon, endpoint.lat]
                    },
                    properties: {
                        ...commonProperties,
                        point_type: 'path_loss_dot',
                        bearing_deg: ray.bearingDeg,
                        distance_m: sample.distanceM,
                        path_loss_db: sample.pathLossDb,
                        margin_db: sample.marginDb,
                        free_space_loss_db: sample.freeSpaceLossDb,
                        terrain_loss_db: sample.terrainLossDb,
                        clutter_loss_db: sample.clutterLossDb,
                        clutter_class: sample.clutterClass,
                        quality_class: qualityClass,
                        dot_opacity: this.dotOpacity
                    }
                });
            }
        }

        return features;
    }

    getDotQualityClass(marginDb) {
        const margin = Number(marginDb);

        if (!Number.isFinite(margin)) return 'unknown';
        if (margin >= 25) return 'excellent';
        if (margin >= 15) return 'good';
        if (margin >= 6) return 'fair';
        return 'marginal';
    }

    createContourFeatures(gatewayLat, gatewayLon, raySamples, commonProperties) {
        const contours = [];
        const ratios = [0.5, 0.75];

        for (const ratio of ratios) {
            const coords = [];

            for (const sample of raySamples) {
                const endpoint = this.destinationPoint(
                    gatewayLat,
                    gatewayLon,
                    sample.bearingDeg,
                    Math.max(this.minRangeM, sample.distanceM * ratio)
                );

                if (endpoint) {
                    coords.push([endpoint.lon, endpoint.lat]);
                }
            }

            if (coords.length < 3) continue;

            coords.push(coords[0]);

            contours.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: coords
                },
                properties: {
                    ...commonProperties,
                    contour_ratio: ratio,
                    contour_range_m: commonProperties.max_range_m * ratio
                }
            });
        }

        return contours;
    }

    ensureSources() {
        if (!this.map) return;

        if (!this.map.getSource(this.sourceId)) {
            this.map.addSource(this.sourceId, {
                type: 'geojson',
                data: EMPTY_FEATURE_COLLECTION
            });
        }

        if (!this.map.getSource(this.contourSourceId)) {
            this.map.addSource(this.contourSourceId, {
                type: 'geojson',
                data: EMPTY_FEATURE_COLLECTION
            });
        }

        if (!this.map.getSource(this.dotSourceId)) {
            this.map.addSource(this.dotSourceId, {
                type: 'geojson',
                data: EMPTY_FEATURE_COLLECTION
            });
        }
    }

    setSourceData(coverageFeatures, contourFeatures, dotFeatures = []) {
        const coverageData = {
            type: 'FeatureCollection',
            features: coverageFeatures || []
        };

        const contourData = {
            type: 'FeatureCollection',
            features: contourFeatures || []
        };

        const dotData = {
            type: 'FeatureCollection',
            features: dotFeatures || []
        };

        const coverageSource = this.map?.getSource(this.sourceId);
        if (coverageSource?.setData) {
            coverageSource.setData(coverageData);
        }

        const contourSource = this.map?.getSource(this.contourSourceId);
        if (contourSource?.setData) {
            contourSource.setData(contourData);
        }

        const dotSource = this.map?.getSource(this.dotSourceId);
        if (dotSource?.setData) {
            dotSource.setData(dotData);
        }
    }

    ensureLayers(beforeId) {
        if (!this.map) return;

        if (!this.map.getLayer(this.fillLayerId)) {
            this.map.addLayer(
                {
                    id: this.fillLayerId,
                    type: 'fill',
                    source: this.sourceId,
                    paint: {
                        'fill-color': '#2aa7ff',
                        'fill-opacity': [
                            'interpolate',
                            ['linear'],
                            ['coalesce', ['get', 'confidence_score'], 0.45],
                            0,
                            0.004,
                            1,
                            0.014
                        ]
                    }
                },
                beforeId
            );
        }

        if (!this.map.getLayer(this.dotLayerId)) {
            this.map.addLayer(
                {
                    id: this.dotLayerId,
                    type: 'circle',
                    source: this.dotSourceId,
                    paint: {
                        'circle-color': [
                            'match',
                            ['get', 'quality_class'],
                            'excellent',
                            '#24f04f',
                            'good',
                            '#92f23a',
                            'fair',
                            '#ffb02e',
                            'marginal',
                            '#ff3b30',
                            '#8bdcff'
                        ],
                        'circle-radius': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            8,
                            Math.max(1.2, this.dotRadiusBase - 0.8),
                            12,
                            this.dotRadiusBase,
                            15,
                            this.dotRadiusBase + 1.2
                        ],
                        'circle-opacity': [
                            'interpolate',
                            ['linear'],
                            ['coalesce', ['get', 'margin_db'], 0],
                            0,
                            Math.max(0.46, this.dotOpacity * 0.86),
                            12,
                            Math.max(0.58, this.dotOpacity * 0.96),
                            25,
                            Math.min(0.92, this.dotOpacity * 1.12)
                        ],
                        'circle-stroke-width': 0,
                        'circle-blur': 0.15
                    }
                },
                beforeId
            );
        }

        if (!this.map.getLayer(this.borderLayerId)) {
            this.map.addLayer(
                {
                    id: this.borderLayerId,
                    type: 'line',
                    source: this.sourceId,
                    paint: {
                        'line-color': '#2aa7ff',
                        'line-width': 1,
                        'line-opacity': 0.10,
                        'line-dasharray': [4, 5]
                    }
                },
                beforeId
            );
        }

        if (!this.map.getLayer(this.contourLayerId)) {
            this.map.addLayer(
                {
                    id: this.contourLayerId,
                    type: 'line',
                    source: this.contourSourceId,
                    paint: {
                        'line-color': '#2aa7ff',
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['get', 'contour_ratio'],
                            0.5,
                            0.55,
                            0.75,
                            0.75
                        ],
                        'line-opacity': 0.1,
                        'line-dasharray': [2, 7]
                    }
                },
                beforeId
            );
        }

        this.attachEventHandlers();
    }

    attachEventHandlers() {
        if (!this.map || this.handlersAttached) return;

        this.map.on('click', this.fillLayerId, this.onFillClick);
        this.map.on('mouseenter', this.fillLayerId, this.onMouseEnter);
        this.map.on('mouseleave', this.fillLayerId, this.onMouseLeave);

        this.handlersAttached = true;
    }

    detachEventHandlers() {
        if (!this.map || !this.handlersAttached) return;

        try {
            this.map.off('click', this.fillLayerId, this.onFillClick);
        } catch {
            // Ignore stale MapLibre handlers during teardown.
        }

        try {
            this.map.off('mouseenter', this.fillLayerId, this.onMouseEnter);
        } catch {
            // Ignore stale MapLibre handlers during teardown.
        }

        try {
            this.map.off('mouseleave', this.fillLayerId, this.onMouseLeave);
        } catch {
            // Ignore stale MapLibre handlers during teardown.
        }

        this.handlersAttached = false;
    }

    setVisible(visible) {
        this.visible = !!visible;
        if (!this.map) return;

        const visibility = this.visible ? 'visible' : 'none';

        for (const layerId of this.getLayerIds()) {
            if (this.map.getLayer(layerId)) {
                this.map.setLayoutProperty(layerId, 'visibility', visibility);
            }
        }
    }

    clear() {
        if (!this.map) return;
        this.setSourceData([], [], []);
    }

    remove() {
        if (!this.map) return;

        this.detachEventHandlers();

        for (const layerId of [this.dotLayerId, this.contourLayerId, this.borderLayerId, this.fillLayerId]) {
            try {
                if (this.map.getLayer(layerId)) {
                    this.map.removeLayer(layerId);
                }
            } catch {
                // Ignore already removed layers.
            }
        }

        for (const sourceId of [this.dotSourceId, this.contourSourceId, this.sourceId]) {
            try {
                if (this.map.getSource(sourceId)) {
                    this.map.removeSource(sourceId);
                }
            } catch {
                // Ignore already removed sources.
            }
        }
    }

    handleFillClick(event) {
        const feature = event?.features?.[0];

        if (!feature || !this.map) {
            return;
        }

        const properties = feature.properties || {};
        const coordinates = event.lngLat;

        new maplibregl.Popup({
            className: 'dashboard-device-popup',
            closeButton: true,
            closeOnClick: true,
            offset: 18,
            maxWidth: '400px'
        })
            .setLngLat(coordinates)
            .setHTML(this.buildPopupHtml(properties))
            .addTo(this.map);
    }

    handleMouseEnter() {
        if (this.map?.getCanvas) {
            this.map.getCanvas().style.cursor = 'pointer';
        }
    }

    handleMouseLeave() {
        if (this.map?.getCanvas) {
            this.map.getCanvas().style.cursor = '';
        }
    }

    buildPopupHtml(properties) {
        const safe = (value, fallback = 'N/A') => this.escapeHtml(value ?? fallback);

        const fixed = (value, digits = 1, fallback = 'N/A') => {
            const numberValue = Number(value);
            return Number.isFinite(numberValue) ? numberValue.toFixed(digits) : fallback;
        };

        return `
      <div class="device-popup-card">
        <div class="device-popup-card-header">
          <div>
            <div class="device-popup-kicker">P.452-inspired Terrain Path-Loss</div>
            <div class="device-popup-title">${safe(properties.gateway_id)}</div>
          </div>
        </div>

        <div class="device-popup-info">
          <div class="device-popup-row">
            <span>Frequency</span>
            <strong>${fixed(properties.frequency_mhz, 0)} MHz</strong>
          </div>

          <div class="device-popup-row">
            <span>Reference sensitivity</span>
            <strong>${fixed(properties.receiver_sensitivity_dbm, 0)} dBm (${safe(properties.reference_spreading_factor)})</strong>
          </div>

          <div class="device-popup-row">
            <span>Max model range</span>
            <strong>${fixed(Number(properties.max_range_m) / 1000, 2)} km</strong>
          </div>

          <div class="device-popup-row">
            <span>Hard range cap</span>
            <strong>${fixed(Number(properties.hard_range_cap_m) / 1000, 2)} km</strong>
          </div>

          <div class="device-popup-row">
            <span>Dot grid spacing</span>
            <strong>${fixed(properties.dot_grid_spacing_m, 0)} m / ${fixed(properties.dot_opacity, 2)} opacity</strong>
          </div>

          <div class="device-popup-row">
            <span>Allowed path loss</span>
            <strong>${fixed(properties.allowed_path_loss_db, 1)} dB</strong>
          </div>

          <div class="device-popup-row">
            <span>Average terrain loss</span>
            <strong>${fixed(properties.average_terrain_loss_db, 1)} dB</strong>
          </div>


          <div class="device-popup-row">
            <span>Avg clutter loss</span>
            <strong>${fixed(properties.average_clutter_loss_db, 1)} dB</strong>
          </div>

          <div class="device-popup-row">
            <span>Blocked bearings</span>
            <strong>${Math.round(Number(properties.terrain_blocked_ratio || 0) * 100)}%</strong>
          </div>

          <div class="device-popup-row">
            <span>Gateway altitude</span>
            <strong>${fixed(properties.gateway_altitude, 1)} m ASL</strong>
          </div>

        </div>
      </div>`;
    }

    getAllowedPathLossDb() {
        return (
            this.deviceTxPowerDbm +
            this.deviceAntennaGainDbi +
            this.gatewayAntennaGainDbi -
            this.receiverSensitivityDbm -
            this.fadeMarginDb
        );
    }

    resolveGatewayGroundElevation(lon, lat, gateway) {
        const sampled = this.getPhysicalTerrainElevation(lon, lat);
        if (Number.isFinite(sampled)) {
            return sampled;
        }

        const stored = Number(gateway?.groundElevation);
        return Number.isFinite(stored) ? stored : 0;
    }

    resolveGatewayAltitude(gateway, groundElevation) {
        const explicitAltitude = Number(gateway?.altitude);

        if (Number.isFinite(explicitAltitude) && explicitAltitude > groundElevation - 10) {
            return explicitAltitude;
        }

        const towerHeight = Number(gateway?.towerHeight);

        if (Number.isFinite(towerHeight) && towerHeight > 0) {
            return groundElevation + towerHeight;
        }

        return groundElevation + this.defaultGatewayHeightM;
    }

    resolveClutterLoss({ gateway, endpoint, bearingDeg, distanceM }) {
        let resolvedClass = null;

        if (this.clutterResolver) {
            try {
                resolvedClass = this.clutterResolver({
                    gateway,
                    endpoint,
                    bearingDeg,
                    distanceM,
                    defaultClutterClass: this.defaultClutterClass
                });
            } catch {
                resolvedClass = null;
            }
        }

        resolvedClass =
            resolvedClass ||
            gateway?.clutterClass ||
            gateway?.environmentClass ||
            gateway?.terrainClass ||
            this.defaultClutterClass;

        const className = this.normalizeClutterClass(resolvedClass);

        const explicitLoss = Number(gateway?.clutterLossDb);
        const lossDb = Number.isFinite(explicitLoss)
            ? explicitLoss
            : finiteNumber(this.clutterLossByClassDb[className], this.fallbackClutterLossDb);

        return {
            className,
            lossDb
        };
    }

    getDominantClutterClass(counts) {
        let dominantClass = this.defaultClutterClass;
        let maxCount = -1;

        for (const [className, count] of counts.entries()) {
            if (count > maxCount) {
                dominantClass = className;
                maxCount = count;
            }
        }

        return dominantClass;
    }

    normalizeClutterClass(value) {
        const raw = String(value || '').trim().toLowerCase();

        const aliases = {
            open: 'open',
            opensky: 'open',
            open_sky: 'open',
            rural: 'rural',
            countryside: 'rural',
            suburban: 'suburban',
            sub_urban: 'suburban',
            suburb: 'suburban',
            urban: 'urban',
            city: 'urban',
            denseurban: 'denseUrban',
            dense_urban: 'denseUrban',
            dense: 'denseUrban',
            industrial: 'industrial',
            factory: 'industrial',
            forest: 'forest',
            woodland: 'forest'
        };

        const availableClasses = this.clutterLossByClassDb || DEFAULT_CLUTTER_LOSS_BY_CLASS_DB;
        const normalized = aliases[raw] || raw || 'suburban';

        if (Object.prototype.hasOwnProperty.call(availableClasses, normalized)) {
            return normalized;
        }

        const fallbackClass = this.defaultClutterClass || 'suburban';

        if (Object.prototype.hasOwnProperty.call(availableClasses, fallbackClass)) {
            return fallbackClass;
        }

        return 'suburban';
    }

    getPhysicalTerrainElevation(lon, lat) {
        try {
            if (!this.map?.queryTerrainElevation) {
                return null;
            }

            if (this.map.getTerrain && !this.map.getTerrain()) {
                return null;
            }

            const elevation = this.map.queryTerrainElevation([lon, lat]);

            if (!Number.isFinite(elevation)) {
                return null;
            }

            if (!this.divideTerrainByExaggeration) {
                return elevation;
            }

            const divisor =
                Number.isFinite(this.terrainExaggeration) && this.terrainExaggeration > 0
                    ? this.terrainExaggeration
                    : 1;

            return elevation / divisor;
        } catch {
            return null;
        }
    }

    destinationPoint(lat, lon, bearingDeg, distanceM) {
        const angularDistance = distanceM / EARTH_RADIUS_M;
        const bearingRad = this.toRadians(bearingDeg);
        const latRad = this.toRadians(lat);
        const lonRad = this.toRadians(lon);

        const sinLat = Math.sin(latRad);
        const cosLat = Math.cos(latRad);
        const sinAngular = Math.sin(angularDistance);
        const cosAngular = Math.cos(angularDistance);

        const destLatRad = Math.asin(
            sinLat * cosAngular + cosLat * sinAngular * Math.cos(bearingRad)
        );

        const destLonRad =
            lonRad +
            Math.atan2(
                Math.sin(bearingRad) * sinAngular * cosLat,
                cosAngular - sinLat * Math.sin(destLatRad)
            );

        const destLat = this.toDegrees(destLatRad);
        const destLon = ((this.toDegrees(destLonRad) + 540) % 360) - 180;

        if (!Number.isFinite(destLat) || !Number.isFinite(destLon)) {
            return null;
        }

        return {
            lat: destLat,
            lon: destLon
        };
    }

    clamp(value, min, max) {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
            return min;
        }

        return Math.max(min, Math.min(max, numericValue));
    }

    toRadians(degrees) {
        return (degrees * Math.PI) / 180;
    }

    toDegrees(radians) {
        return (radians * 180) / Math.PI;
    }

    escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}